// Key-value storage that survives the APK's origin switch.
//
// In the Android app, the live site runs at https://mlcu.scanix.in, but when there's no
// network at launch the bundled fallback runs at https://localhost. Browser storage (localStorage,
// IndexedDB) is per-origin, so data written on one is invisible on the other. Native storage
// (Capacitor Preferences) is shared by the whole app, so cached login, payment cache and the
// offline queue stay available on the fallback.
//
// On the plain web, this falls back to localStorage, so behavior there is unchanged.

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

const isNative = () => Capacitor.isNativePlatform();

function legacyLocalStorageGet(key) {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

export async function storeGet(key) {
    if (!isNative()) return legacyLocalStorageGet(key);

    const { value } = await Preferences.get({ key });
    if (value !== null && value !== undefined) return value;

    // One-time migration: data written by the live origin's localStorage before this change
    // (when running in the APK) gets copied into native storage the first time it's read.
    const legacy = legacyLocalStorageGet(key);
    if (legacy !== null) {
        await Preferences.set({ key, value: legacy });
    }
    return legacy;
}

export async function storeSet(key, value) {
    if (!isNative()) {
        try {
            localStorage.setItem(key, value);
        } catch {
            // storage full/unavailable — nothing sensible to do
        }
        return;
    }
    await Preferences.set({ key, value });
}

export async function storeRemove(key) {
    if (!isNative()) {
        try {
            localStorage.removeItem(key);
        } catch {
            // ignore
        }
        return;
    }
    await Preferences.remove({ key });
}

export async function storeGetJSON(key, fallback = null) {
    try {
        const raw = await storeGet(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

export async function storeSetJSON(key, value) {
    await storeSet(key, JSON.stringify(value));
}
